// Quick purchase (F-FE-N97): "bought and brought back the same day" — market
// runs, cash & carry. Replaces the old PurchaseForm behind the Inventory tab's
// Purchase button and the Alerts tab's Restock button, both of which recorded
// stock and cost but NEVER what you now owe the vendor. This one goes through
// /store/quick-purchase, so stock and the payable always move together.
import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import type { StockItem, Supplier } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatDateHuman } from '@/lib/utils'
import { useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { PaidFromChoice, type PaidFrom } from './purchasing/PaidFromChoice'
import { ExpiryDateField } from './inventory/ExpiryFields'
import { businessTodayIso, defaultExpiryFor, expiryMissing } from '@/lib/expiry'
import { useDayBoundaryHour } from '@/hooks/useDayBoundaryHour'

type ToastFn = (type: 'success' | 'error', message: string) => void

export function QuickPurchaseDialog({
  item,
  onClose,
  showToast,
}: {
  item: StockItem | null
  onClose: () => void
  showToast: ToastFn
}) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const open = !!item

  const { data: supRes } = useQuery({ queryKey: ['suppliers'], queryFn: () => apiClient.listSuppliers(), enabled: open })
  const suppliers: Supplier[] = supRes?.data ?? []

  const [supplierId, setSupplierId] = useState('')
  const [qty, setQty] = useState('')
  const [unitCost, setUnitCost] = useState('')
  // Expiry (F-CTRL-N73): a tracked item needs a date; its shelf life fills
  // one in until somebody types their own.
  const [typedExpiry, setTypedExpiry] = useState<string | null>(null)
  const today = businessTodayIso(useDayBoundaryHour())
  const expiry = typedExpiry ?? defaultExpiryFor(item, today)
  // Kitchen flow lane D: on credit (today) or paid from petty cash.
  const kitchenFlow = useKitchenFlowEnabled()
  const [paidFrom, setPaidFrom] = useState<PaidFrom>('vendor_credit')

  useEffect(() => {
    if (!item) return
    setSupplierId(item.supplier_id ?? '')
    setQty('')
    setUnitCost((item.default_unit_cost ?? 0) > 0 ? String(item.default_unit_cost) : '')
    setTypedExpiry(null)
    setPaidFrom('vendor_credit')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id])

  // One stable idempotency key per open dialog — a retry dedups server-side.
  const idemKey = useMemo(() => (item ? crypto.randomUUID() : ''), [item?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const qtyNum = parseFloat(qty)
  const costNum = parseFloat(unitCost)
  const total = Number.isFinite(qtyNum) && Number.isFinite(costNum) && qtyNum > 0 && costNum >= 0 ? qtyNum * costNum : 0

  const purchase = useMutation({
    mutationFn: () => {
      if (!item) throw new Error('No item selected')
      return apiClient.quickPurchase({
        supplier_id: supplierId,
        lines: [{ stock_item_id: item.id, quantity: qtyNum, unit_cost: costNum, expiry_date: expiry || undefined }],
        idempotency_key: idemKey || undefined,
        ...(kitchenFlow ? { paid_from: paidFrom } : {}),
      })
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockSummary'] })
      qc.invalidateQueries({ queryKey: ['stockMovements'] })
      qc.invalidateQueries({ queryKey: ['stockAlerts'] })
      qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      qc.invalidateQueries({ queryKey: ['inventoryActivity'] })
      qc.invalidateQueries({ queryKey: ['expiringBatches'] }) // F-FE-N118
      qc.invalidateQueries({ queryKey: ['stockItem'] })
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      qc.invalidateQueries({ queryKey: ['vendorBills'] })
      qc.invalidateQueries({ queryKey: ['vendorLedger'] })
      const bill = res.data?.bill
      const vendorName = res.data?.supplier_name ?? suppliers.find((s) => s.id === supplierId)?.name ?? 'the vendor'
      if (res.data?.bill_skipped_reason === 'petty_cash') {
        showToast('success', `Added ${qty} ${item?.unit ?? ''} ${item?.name ?? ''} · paid ${formatCurrency(res.data?.received_value ?? total)} from petty cash to ${vendorName}.`)
      } else if (bill) {
        showToast(
          'success',
          `Added ${qty} ${item?.unit ?? ''} ${item?.name ?? ''} · ${formatCurrency(bill.amount)} owed to ${vendorName}${bill.due_date ? `, due ${formatDateHuman(bill.due_date)}` : ''}. If it was paid on the spot, record the payment on their page.`,
        )
      } else if (res.data?.bill_skipped_reason === 'consignment_supplier') {
        showToast('success', `Added ${qty} ${item?.unit ?? ''} ${item?.name ?? ''} — consignment stock, no bill; you settle ${vendorName} from sales.`)
      } else {
        showToast('success', `Added ${qty} ${item?.unit ?? ''} ${item?.name ?? ''}`)
      }
      onClose()
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed to record the purchase'),
  })

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl">Record a purchase — {item?.name}</DialogTitle>
          <DialogDescription>
            For things bought and brought back the same day. Stock and what you owe the vendor
            update together.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label htmlFor="qp-vendor">Vendor</Label>
            <select
              id="qp-vendor"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
            >
              <option value="">Select vendor…</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Bazaar run with no fixed vendor? Add a one-time vendor like &quot;Open market&quot; on the
              Vendors tab — then what you spend there is tracked too.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="qp-qty">Quantity{item?.unit ? ` (${item.unit})` : ''}</Label>
              <Input
                id="qp-qty"
                type="number"
                step="0.01"
                min="0"
                className="tabular-nums"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="qp-cost">Unit cost</Label>
              <Input
                id="qp-cost"
                type="number"
                step="0.01"
                min="0"
                className="tabular-nums"
                value={unitCost}
                onChange={(e) => setUnitCost(e.target.value)}
              />
            </div>
          </div>
          {kitchenFlow && <PaidFromChoice value={paidFrom} onChange={setPaidFrom} />}
          <ExpiryDateField
            id="qp-expiry"
            item={item}
            value={expiry}
            onChange={setTypedExpiry}
            prefilled={typedExpiry === null}
          />
        </div>
        <DialogFooter className="gap-2 sm:items-center sm:gap-3">
          {total > 0 && (
            <span className="text-sm text-muted-foreground sm:mr-auto">
              Total <span className="font-semibold text-foreground tabular-nums">{formatCurrency(total)}</span>
            </span>
          )}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!supplierId || !(qtyNum > 0) || !Number.isFinite(costNum) || costNum < 0 || expiryMissing(item, expiry) || purchase.isPending}
            title={!supplierId ? 'Pick a vendor — the purchase records what you now owe them' : expiryMissing(item, expiry) ? 'Enter the expiry date of this stock' : undefined}
            onClick={() => purchase.mutate()}
          >
            {purchase.isPending ? 'Recording…' : 'Record purchase'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
